import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { usePractitionersOus } from 'api/queries/marketing-capabilities/practitioners/usePractitionersOUs'
import { useOnboardingFilters } from 'api/queries/marketing-capabilities/resources/useOnboardingFilters'
import { useSkills } from 'api/queries/marketing-capabilities/skills/useSkills'
import { alphaSortOptions, SortValue } from 'pages/exploreLearnings/constants/sortOptions.constants'

import { Button } from './Button'
import { SelectMultiple } from './SelectMultiple'
import { SelectSingle } from './SelectSingle'
import { SortButton } from './SortButton'
import { useOnboardingRoute } from '../hooks/useOnboardingRoute'

interface ResourcesFiltersProps {
  resourcesState: string
  ouState: string[]
  skillState: string[]
  onboardingState: string
  onApplyFilters: (onboarding: string, ous: string[], skills: string[]) => void
  onClearAllFilters: () => void
  onSort: (value: string) => void
}

export const ResourcesFilters = ({
  ouState,
  skillState,
  onboardingState,
  onApplyFilters,
  onClearAllFilters,
  onSort,
}: ResourcesFiltersProps) => {
  const [tempOnboarding, setTempOnboarding] = useState<string>(onboardingState)
  const [tempOus, setTempOus] = useState<string[]>(ouState)
  const [tempSkills, setTempSkills] = useState<string[]>(skillState)
  const navigate = useNavigate()

  useEffect(() => {
    onSort(SortValue.AZ)
  }, [onSort])

  const { data: ouList } = usePractitionersOus()
  const { data: onboardingFilters } = useOnboardingFilters()
  const { data: skillList } = useSkills(true)

  useEffect(() => {
    setTempOnboarding(onboardingState)
  }, [onboardingState])

  useEffect(() => {
    let ous = ouState
    if (ouList) {
      const validIds = ouList.map((ou: any) => ou.id)
      ous = ous.filter(id => validIds.includes(id))
    }
    setTempOus(ous)
    setTempSkills(skillState)
  }, [ouState, skillState, ouList])

  useOnboardingRoute({
    setTempOnboarding,
    setTempSkills,
    onApplyFilters,
    ous: ouState,
    skills: skillState,
    skillList,
  })

  const emptyFilters = tempOus.length === 0 && tempSkills.length === 0 && tempOnboarding === 'false'

  const getSkillLabel = (id: string) =>
    (Array.isArray(skillList) ? skillList : skillList?.data)?.find((skill: any) => skill.id === id)?.name || id

  const handleApply = () => {
    onApplyFilters(tempOnboarding, tempOus, tempSkills)
    const params = new URLSearchParams()

    if (tempOnboarding === 'true') {
      params.set('onboarding', 'true')
    }

    const skillLabels = tempSkills.map(skillId => getSkillLabel(skillId))
    if (skillLabels.length > 0) {
      params.set('skills', skillLabels.join(','))
    }

    const search = params.toString()
    navigate(`/explore-learnings/resources${search ? `?${search}` : ''}`, { replace: true })
  }

  const validOuIds = ouList ? ouList.map((ou: any) => ou.id) : []
  const effectiveOuState = ouState.filter(id => validOuIds.includes(id))

  const hasSameFilters =
    tempOus.every(ou => effectiveOuState.includes(ou)) &&
    tempOus.length === effectiveOuState.length &&
    tempSkills.every((skill: any) => skillState.includes(skill)) &&
    tempSkills.length === skillState.length &&
    tempOnboarding === onboardingState

  const handleClear = () => {
    setTempOnboarding('false')
    setTempOus([])
    setTempSkills([])
    onClearAllFilters()
    navigate('/explore-learnings/resources', { replace: true })
  }

  return (
    <div className="w-[1120px] mx-auto flex justify-between pt-12 pb-8">
      <div className="flex">
        <div className="mr-2">
          <SelectSingle
            selectValue={tempOnboarding}
            onChange={value => {
              setTempOnboarding(value)
            }}
            options={onboardingFilters?.map((option: { id: string; name: string }) => {
              return { key: option.name, value: option.id }
            })}
            placeholder="All resources"
          />
        </div>
        {ouList && (
          <div className="mr-2">
            <SelectMultiple
              options={
                ouList?.map((ou: any) => {
                  return { key: ou.name, value: ou.id }
                }) || []
              }
              selectValue={tempOus}
              onChange={(OU: string[]) => {
                setTempOus(OU)
              }}
              placeholder="OU"
              searchPlaceholder="E.g. EU"
            />
          </div>
        )}
        {skillList && (
          <div className="mr-2">
            <SelectMultiple
              options={
                (Array.isArray(skillList) ? skillList : skillList.data)?.map((skill: any) => {
                  return { key: skill.name, value: skill.id }
                }) || []
              }
              selectValue={tempSkills}
              onChange={(skills: string[]) => {
                setTempSkills(skills)
              }}
              placeholder="Skill"
              searchPlaceholder="E.g. Science"
            />
          </div>
        )}
        <Button
          text="Apply"
          onClick={handleApply}
          disabled={hasSameFilters}
          className="mx-2  px-[24px] py-[13px] leading-none disabled:bg-[#D4D4D4] disabled:text-[#505050]"
        />
        <button
          className={`${emptyFilters ? 'text-[#9B9B9B]' : ''} font-TCCCText font-semibold ml-2`}
          onClick={handleClear}
          disabled={emptyFilters}
        >
          Clear all
        </button>
      </div>
      <SortButton options={alphaSortOptions} onChange={onSort} />
    </div>
  )
}
